import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { KeyExpression } from '../../../../platform/input/KeyExpression';
import { Colors } from '../../../../ui';
import { useResolve } from '../../../Container';
import { AdapterInfo } from '../../AdapterInfo';
import { EditorSession } from '../../EditorSession';
import { move } from '../../commands/TransformCommands';
import { PreferredTextColor, TextAlignment, TextSettings, textAlignmentToTextAlign } from '../../entities/TextConventions';
import { UIField, UIFieldProps } from '../../entities/UIField';
import { KeyMappings } from '../../tooling/KeyMappings';
import { TextRuler } from '../../visualization/TextRuler';

const nextFieldExpr = KeyExpression.parse('TAB');
const prevFieldExpr = KeyExpression.parse('TAB+SHIFT');
const closeOutExpr = KeyExpression.parse('CTRL+ENTER');

const moveMappings = KeyMappings.from(
    ['LEFT_ARROW', [move, ['left', 'grid']]],
    ['LEFT_ARROW+CTRL', [move, ['left', 'unit']]],
    ['LEFT_ARROW+SHIFT', [move, ['left', 'bounds']]],
    ['UP_ARROW', [move, ['up', 'grid']]],
    ['UP_ARROW+CTRL', [move, ['up', 'unit']]],
    ['UP_ARROW+SHIFT', [move, ['up', 'bounds']]],
    ['RIGHT_ARROW', [move, ['right', 'grid']]],
    ['RIGHT_ARROW+CTRL', [move, ['right', 'unit']]],
    ['RIGHT_ARROW+SHIFT', [move, ['right', 'bounds']]],
    ['DOWN_ARROW', [move, ['down', 'grid']]],
    ['DOWN_ARROW+CTRL', [move, ['down', 'unit']]],
    ['DOWN_ARROW+SHIFT', [move, ['down', 'bounds']]],
);

const computeMargin = (value: number | number[]) =>
    Array.isArray(value) ? value.map(x => x + 'px').join(' ') : value + 'px';

const computeFontColor = (field: UIField, settings: TextSettings) => (
    settings.fontColor ||
    field.owner.as(PreferredTextColor)?.getPreferredTextColor() ||
    Colors.text
);

const computeFontSize = (field: UIField, settings: TextSettings, value: string) => (
    settings.fontSize || (
        Math.min(settings.maxFontSize ?? 128, TextRuler.fit(
            value,
            settings,
            field.bounds.width,
            field.bounds.height,// hasWidgets ? 92 : 95,
        ))
    )
);

const computeStyling = (field: UIField, settings: TextSettings, value: string): any => {
    const fontColor = computeFontColor(field, settings);
    const fontSize = computeFontSize(field, settings, value);
    const styling: any = {
        position: 'absolute',
        left: '0px',
        width: field.bounds.width + 'px',
        color: fontColor,
        fontFamily: settings.fontFamily,
        fontSize: fontSize + 'px',
        fontStyle: settings.fontStyle,
        fontWeight: `${settings.fontWeight}`,
        fontVariantNumeric: 'tabular-nums',
        lineHeight: (fontSize * settings.lineHeight) + 'px',
        padding: computeMargin(settings.textMargin),
        textAlign: textAlignmentToTextAlign(settings.textAlignH),
    };

    if (settings.textAlignV === TextAlignment.start) {
        styling.top = '0px';
        styling.height = field.bounds.height + 'px';
    }
    else {
        const textHeight = TextRuler.measureHeight(value, { ...settings, fontSize }, field.bounds.width);
        if (settings.textAlignV === TextAlignment.middle) {
            styling.top = ((field.bounds.height - textHeight) / 2) + 'px';
            styling.height = textHeight + 'px';
        }
        else /* bottom */ {
            styling.bottom = '0px';
            styling.height = textHeight + 'px';
        }
    }

    return styling;
};

export const TextAreaFrame = styled.div<{ active: boolean, borderRadius?: number }>`
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: ${p => p.borderRadius ?? 2}px;
    &:hover {
        box-shadow: 0 0 0 3px rgba(164,202,254,.45);
        cursor: pointer;
    }
    ${p => !p.active ? '' : css`
        box-shadow: 0 0 0 3px rgba(164,202,254,.45);
    `}
`;

const TextArea = styled.textarea`
    width: 100%;
    height: 100%;    
    box-sizing: border-box;
    padding: 0;
    margin: 0;
    border: none;
    resize: none;
    overflow: hidden;
    background: transparent;
`;

export interface SimpleTextFieldInputProps extends UIFieldProps {
    borderRadius?: number;
    settings: TextSettings;
    value: string;
    maxLength?: number;
    onChange(value: string): void;
    onCommit?(finalValue: string): void;
};

export const SimpleTextFieldInput = React.memo<SimpleTextFieldInputProps>(props => {
    const { active, field, settings, value, maxLength, borderRadius, onChange, onCommit } = props;

    const session = useResolve(EditorSession);
    const styling = computeStyling(field, settings, value);
    const inputRef = React.useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (active && !!inputRef.current && !inputRef.current.matches(':focus')) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [active, inputRef.current]);

    const onFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
        const curr = session.get(AdapterInfo);
        if (curr?.name === field.name) return;
        session.patch(AdapterInfo, { name: field.name });
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // if (!!this.props.field.value) return;
        // const [_, callback] = moveMappings.find(([expr]) => expr.matches(e)) ?? [];
        // if (!callback) return;
        // e.preventDefault();
        // this.objects.update(u => {
        //     u.set('key', updateKey(this.props.field));
        //     u.set('rolling', true);
        //     callback(this.dispatch);
        // });
    };

    // Only want to trigger onCommit when the component is unmounting, but we need to capture the value at the time of unmount
    // so we use a ref to store the value and a useEffect to trigger the onCommit when the component is unmounting.
    const valRef = useRef<string>();
    valRef.current = value;
    useEffect(() => () => onCommit(valRef.current), []);

    return <TextAreaFrame active={active} borderRadius={borderRadius}>
        <TextArea
            ref={inputRef}
            data-testid={field.name}
            role="textbox"
            aria-label={field.name}
            style={styling}
            onFocus={onFocus}
            onChange={e => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            maxLength={maxLength}
            value={value}
        />
    </TextAreaFrame>;
});
